import { useRef, useEffect, useState, useCallback } from 'react';
import { Mutex } from 'async-mutex';

export const useAsyncBlock = (origin?: string) => {
  const mutexRef = useRef(new Mutex());
  // React state needed because mutex.isLocked() isn't reactive - React won't re-render when mutex state changes
  const [isBlocked, setIsBlocked] = useState(false);
  // Prevent setState on unmounted components during async operations
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (origin) {
      mutexRef.current = new Mutex();
      setIsBlocked(false);
    }
  }, [origin]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const blockingAction = useCallback(async (cb: () => unknown) => {
    // Use mutex for race condition protection
    if (mutexRef.current.isLocked()) return null;

    const release = await mutexRef.current.acquire();

    if (isMountedRef.current) {
      setIsBlocked(true);
    }

    let res = null;
    try {
      res = await cb();
    } finally {
      release();
      if (isMountedRef.current) {
        setIsBlocked(false);
      }
    }
    return res;
  }, []);

  return {
    blockingAction,
    isBlocked,
  };
};
